import { festeUrl, istWartend } from "./fotos.js";


/* ═══════════════════════════════════════════════════════════════
   Etiketten für den Offline-Betrieb vorladen

   Der Dienst-Arbeiter legt ein Bild nur ab, wenn es tatsächlich
   angezeigt wurde — und `loading="lazy"` sorgt dafür, dass beim
   Blättern längst nicht alles geholt wird. Ohne Netz fehlte dann
   genau das Etikett, das man sehen wollte.

   Also werden die Vorschaubilder aktiv geholt. Das lohnt sich, weil
   es **einmalig** ist: Jeder Upload bekommt einen eigenen,
   unveränderlichen Pfad. Ein einmal geladenes Etikett wird nie wieder
   geholt; nur wirklich neue Bilder kosten Daten.

   Nur die Vorschau (400 px lange Kante), nicht das Vollbild. Die
   Vorschau deckt Keller-, Wein- und Jahrgangsliste ab und wiegt einen
   Bruchteil; die Großansicht bleibt dem Zufallstreffer überlassen.
   Bei einem Keller mit einigen hundert Etiketten geht es so um
   wenige Megabyte statt um Dutzende — der Unterschied zwischen
   „läuft über Mobilfunk mit" und „macht man besser nicht".
   ═══════════════════════════════════════════════════════════════ */

const BILD_CACHE = "winelake-bilder-v1";
const GLEICHZEITIG = 4;   /* schont Verbindung und Server */


/* Pfad → URL, ohne Serverabruf (siehe dienste/fotos.js). Dieselbe
   Funktion wie dort — weichen beide ab, findet der Vorlader seine
   Bilder im Zwischenspeicher nicht wieder. */
const urlFuer = festeUrl;


/** Alle Vorschaupfade des Kellerbuchs, ohne Dubletten. */
function vorschauPfade(db) {
  const pfade = new Set();
  for (const w of (db && db.weine) || []) {
    for (const jg of w.jahrgaenge || []) {
      const bild = jg.foto || jg.katalogFoto;
      if (!bild) continue;
      const pfad = bild.thumb || bild.pfad;
      if (pfad && !istWartend(pfad)) pfade.add(pfad);
    }
  }
  /* Vom Rundum-Etikett nur die kleine Fassung (512 px) — mit ihr
     dreht sich die Flasche auch ohne Netz, nur etwas weicher. */
  for (const w of (db && db.weine) || []) {
    for (const jg of w.jahrgaenge || []) {
      const r = jg.rundum;
      const pfad = r && (r.thumb || r.pfad);
      if (pfad && !istWartend(pfad)) pfade.add(pfad);
    }
  }
  return [...pfade];
}


const nutzbar = () => typeof caches !== "undefined";


/** Wie viele Vorschaubilder fehlen noch? Reine Cache-Abfragen, kein
    Netz — taugt daher auch für eine Anzeige vor dem Laden. */
async function fehlendeEtiketten(db) {
  if (!nutzbar()) return [];
  let c;
  try { c = await caches.open(BILD_CACHE); } catch (e) { return []; }
  const fehlt = [];
  for (const pfad of vorschauPfade(db)) {
    try { if (!(await c.match(urlFuer(pfad)))) fehlt.push(pfad); } catch (e) { /* überspringen */ }
  }
  return fehlt;
}


/** Fehlende Vorschaubilder holen. Der Dienst-Arbeiter legt sie dabei
    ab — deshalb genügt hier ein schlichtes `fetch`. Läuft im
    Hintergrund und meldet nur, wie viele es wurden. */
async function etikettenVorladen(db, beiFortschritt) {
  const fehlt = await fehlendeEtiketten(db);
  if (fehlt.length === 0) return { geladen: 0, gesamt: 0 };

  let geladen = 0;
  let naechster = 0;
  const arbeiter = async () => {
    while (naechster < fehlt.length) {
      const pfad = fehlt[naechster++];
      try {
        const r = await fetch(urlFuer(pfad), { credentials: "same-origin" });
        if (r && r.ok) geladen++;
        else if (r && (r.status === 401 || r.status === 404)) {
          /* Sitzung weg oder Bild gelöscht — weitermachen wäre sinnlos
             bzw. betrifft nur diesen einen Pfad. */
          if (r.status === 401) return;
        }
      } catch (e) {
        return; /* Netz weg: abbrechen, beim nächsten Abgleich weiter */
      }
      if (beiFortschritt) beiFortschritt(geladen, fehlt.length);
    }
  };
  await Promise.all(Array.from({ length: Math.min(GLEICHZEITIG, fehlt.length) }, arbeiter));
  return { geladen, gesamt: fehlt.length };
}


/** Bilder wegräumen, die im Kellerbuch nicht mehr vorkommen — sonst
    wächst der Zwischenspeicher mit jedem ersetzten Etikett. */
async function etikettenAufraeumen(db) {
  if (!nutzbar()) return 0;
  try {
    const c = await caches.open(BILD_CACHE);
    const erlaubt = new Set();
    for (const w of (db && db.weine) || []) {
      for (const jg of w.jahrgaenge || []) {
        for (const bild of [jg.foto || jg.katalogFoto, jg.rundum]) {
          if (!bild) continue;
          if (bild.pfad && !istWartend(bild.pfad)) erlaubt.add(urlFuer(bild.pfad));
          if (bild.thumb && !istWartend(bild.thumb)) erlaubt.add(urlFuer(bild.thumb));
        }
      }
    }
    let weg = 0;
    for (const anfrage of await c.keys()) {
      if (!erlaubt.has(new URL(anfrage.url).pathname)) { await c.delete(anfrage); weg++; }
    }
    return weg;
  } catch (e) { return 0; }
}


export { etikettenAufraeumen, etikettenVorladen, fehlendeEtiketten, vorschauPfade };
